import type { AudienceType } from "./mandatory-demographics";
import type { AudienceTier } from "./audience-runtime";
import type { ContextMaterialIndexingStatus } from "./context-materials";

/**
 * A single condition within an audience tag.
 * All conditions in a tag are combined with AND logic:
 * a respondent must match every condition to receive the tag.
 */
export interface AudienceTagCondition {
  sourceColumn: string;
  values: Array<string>;
}

/**
 * A user-defined audience tag that groups respondents by multi-column criteria.
 * Tags label twins across the platform (audience summary, chats, studies).
 */
export interface AudienceTag {
  id: string;
  name: string;
  conditions: Array<AudienceTagCondition>;
  color?: string;
  /** LLM-generated plain-language description, persisted on first view. */
  description?: string | null;
}

export type AudienceTagCountStatus = "pending" | "ready" | "unavailable";

export interface AudienceTagCountPreview {
  matchingCount: number;
  totalCount: number;
}

/**
 * Quick Study subsegment-cuts opt-in, scoped to one study session. When
 * `segmentAwareMode` is on, the marketer picked saved `chosenTags` (cap 5) to cut the
 * analysis by; the FE draws a whole-population baseline plus one filtered pool per chosen
 * tag, labels each twin with the cut it was drawn for, and runs ONE ordinary run sliced
 * by segment in analysis. OFF defaults (`false` / `[]`) keep a non-segment-aware session
 * byte-identical to today.
 */
export interface SegmentAwareSession {
  segmentAwareMode: boolean;
  chosenTags: Array<AudienceTag>;
}

/** Matches scripts/narrative-card/schemas.py NarrativeCard (Pydantic by_alias=True output) */
export interface NarrativeCardDraft {
  header: string;
  subheader: string;
  bodyParagraph1: string;
  bodyParagraph2: string | null;
  audienceProfile: Array<{ field: string; value: string }>;
  topLineInsights: Array<{ title: string; description: string }>;
  meta: {
    n: number;
    audienceType: string;
    model: string;
    generatedAt: string;
    findingType: string;
  };
}

export type NarrativeCardStatus = "pending" | "generating" | "ready" | "failed";

export type AudienceImportSessionStatus =
  | "draft"
  | "awaiting_sheet_assignment"
  | "analyzing"
  | "preview_ready"
  | "needs_fix"
  | "creating"
  | "finalizing"
  | "completed"
  | "failed"
  | "expired";

export interface PreviewResolvedIdentity {
  displayName?: string | null;
  gender?: string | null;
  age?: string | null;
  country?: string | null;
}

export interface PreviewRespondentAttribute {
  key: string;
  label: string;
  value: string;
  group?: string;
  flagged?: boolean;
}

export interface PreviewRespondent {
  previewId: string;
  label: string;
  confidence: "high" | "medium" | "low";
  resolvedIdentity: PreviewResolvedIdentity | null;
  compressedAttributes: PreviewRespondentAttribute[];
  compressedAttributesKey?: string | null;
  notes?: string[];
}

/**
 * Single "featured twin" selected from the respondent pool for the preview
 * hero card. Rendered instantly with deterministic fields (label, fallbackTagline,
 * resolvedIdentity, compressedAttributes), then progressively enriched with
 * LLM-generated fields (personaName, imageUrl, tagline, summary).
 */
export interface FeaturedPreviewTwin {
  previewId: string;
  label: string;
  personaName: string | null;
  imageUrl: string | null;
  tagline: string | null;
  fallbackTagline: string;
  summary: string | null;
  resolvedIdentity: PreviewResolvedIdentity;
  compressedAttributes: PreviewRespondentAttribute[];
  compressedAttributesKey?: string | null;
  confidence: "high" | "medium" | "low";
}

export type FeaturedTwinEnrichmentStatus =
  | "pending"
  | "in_progress"
  | "ready"
  | "failed";

export type PreviewReviewState =
  | "awaiting_durable_preview"
  | "awaiting_narrative_card"
  | "enriching_featured_twin"
  | "ready"
  | "degraded";

/**
 * Preview step gating for the import wizard.
 *
 * - `isReady`: durable preview is usable — show the review UI and allow audience
 *   creation when other flow checks pass. True even when `state` is `degraded`.
 * - `state === 'degraded'` with `degradedReasons`: non-blocking warnings (e.g.
 *   narrative LLM failed, featured twin enrichment failed); user may proceed with
 *   fallbacks or retry flows.
 */
export interface PreviewReviewReadiness {
  state: PreviewReviewState;
  isReady: boolean;
  featuredTwinEnrichmentStatus: FeaturedTwinEnrichmentStatus;
  degradedReasons?: Array<"featured_twin" | "narrative_card">;
}

export interface GuidedFixCandidateColumn {
  sourceColumn: string;
  label: string;
  sampleValues: string[];
  selected?: boolean;
  /**
   * When this column is already bound as the source for another canonical
   * identity field, the canonical id of that field (e.g. `std_dem_gender`).
   * Consumers render a subtle hint ("Also used for Gender") next to the row.
   * RND-2299 — avoids silent column-in-use collisions.
   */
  assignedToCanonicalId?: string | null;
  /** Human-readable label for {@link assignedToCanonicalId} (e.g. "Gender"). */
  assignedToLabel?: string | null;
}

export type RequiredFieldStatus =
  | "resolved"
  | "inferred"
  | "constant"
  | "not_provided"
  | "needs_source"
  | "needs_value_review";

export type RequiredFieldState =
  | "needs_source"
  | "needs_confirmation"
  | "needs_value_mapping"
  | "ready_from_source"
  | "ready_from_constant"
  | "ready_from_runtime"
  | "ready_from_inference";

export type RequiredFieldNextAction =
  | "confirm_source"
  | "choose_source"
  | "map_values"
  | "set_constant"
  | "review_inference"
  | "done";

export type RequiredFieldDecisionKind =
  | "source_column"
  | "constant"
  | "accepted_inference";

export type RequiredFieldDecisionSource =
  | "runtime_resolved"
  | "source_column"
  | "constant"
  | "accepted_inference"
  | "not_provided"
  | "unset";

export type GuidedFix =
  | {
      kind: "choose_column";
      canonicalId: string;
      label: string;
      contextKey?: string | null;
      allowMarkNotProvided?: boolean;
      currentSourceColumn?: string | null;
      candidates: GuidedFixCandidateColumn[];
    }
  | {
      kind: "set_constant";
      contextKey: string;
      label: string;
      currentValue?: string | null;
      suggestedValue?: string | null;
      placeholder?: string | null;
    }
  | {
      kind: "review_values";
      canonicalId: string;
      label: string;
      sourceColumn: string;
      currentValueMap?: Record<string, string>;
      distinctValues: string[];
      sampleValues: string[];
      previewRows?: Array<Record<string, unknown>>;
      allColumnNames?: string[];
    }
  | {
      kind: "mark_not_provided";
      contextKey: string;
      label: string;
      value: string;
    };

export interface PreviewIssue {
  id: string;
  severity: "blocking" | "warning" | "info";
  title: string;
  description: string;
  fixLabel: string | null;
  fixType:
    | "choose_column"
    | "review_values"
    | "set_constant"
    | "mark_not_provided";
  affectedPreviewIds: string[];
  guidedFix: GuidedFix | null;
}

export interface RequiredFieldCoverage {
  resolvedCount: number;
  inferredCount: number;
  totalCount: number;
}

export interface RequiredFieldValueMappingSummary {
  mappedCount: number;
  unmappedCount: number;
}

export interface RequiredFieldSuggestedInference {
  summary: string;
  values: Array<{
    value: string;
    percentage: number;
  }>;
}

export interface RequiredFieldSessionItem {
  canonicalId: string;
  label: string;
  status: RequiredFieldStatus;
  state: RequiredFieldState;
  decisionSource: RequiredFieldDecisionSource;
  satisfied: boolean;
  guidance: string;
  currentSourceColumn: string | null;
  currentConstantValue: string | null;
  allowNotProvided: boolean;
  allowInference: boolean;
  inferenceAccepted: boolean;
  nextAction: RequiredFieldNextAction;
  requiresConfirmation: boolean;
  requiresValueMapping: boolean;
  allowedDecisionKinds: RequiredFieldDecisionKind[];
  sampleRawValues: string[];
  normalizedExamples: string[];
  valueMappingSummary: RequiredFieldValueMappingSummary;
  suggestedInference: RequiredFieldSuggestedInference | null;
  coverage: RequiredFieldCoverage;
  chooseColumnFix: Extract<GuidedFix, { kind: "choose_column" }>;
  reviewValuesFix?: Extract<GuidedFix, { kind: "review_values" }> | null;
}

export type IdentityFieldSummary = Omit<
  RequiredFieldSessionItem,
  "chooseColumnFix" | "reviewValuesFix"
>;

export type IdentityWorkspaceMode =
  | "confirm_source"
  | "set_constant"
  | "review_inference"
  | "map_values";

export interface IdentityFieldWorkspace {
  canonicalId: string;
  field: IdentityFieldSummary;
  availableModes: IdentityWorkspaceMode[];
  defaultMode: IdentityWorkspaceMode;
  chooseColumnFix: Extract<GuidedFix, { kind: "choose_column" }>;
  candidateWindow: {
    totalCount: number;
    returnedCount: number;
    offset: number;
    limit: number;
    hasMore: boolean;
    query: string | null;
  };
  reviewValuesFix?: Extract<GuidedFix, { kind: "review_values" }> | null;
}

export interface IdentitySourceOptions {
  canonicalId: string;
  currentSourceColumn: string | null;
  candidates: GuidedFixCandidateColumn[];
  candidateWindow: {
    totalCount: number;
    returnedCount: number;
    offset: number;
    limit: number;
    hasMore: boolean;
    query: string | null;
  };
  /**
   * Phase 0 column groups from preflight (operational columns stripped).
   * When set, identity "Map to question" pickers can mirror segment-filter grouping.
   */
  segmentFilterColumnGroups?: Array<AudienceImportSegmentFiltersColumnGroup> | null;
  /**
   * Column names preflight flagged as operational/noisy (e.g. "Captured variable …").
   * Exposed so the identity "Map to question" picker can apply the same filter that
   * the segmentation picker applies, keeping both dropdowns consistent.
   */
  segmentFilterOperationalColumns?: string[] | null;
}

export interface IdentityValueMapWorkspace {
  canonicalId: string;
  sourceColumn: string | null;
  currentValueMap: Record<string, string>;
  distinctValues: string[];
  sampleValues: string[];
  previewRows?: Array<Record<string, unknown>>;
  allColumnNames?: string[];
  /**
   * Display-only code -> label dictionary for SPSS/XLSX value labels.
   * Keys match raw codes in `distinctValues` / `currentValueMap`.
   * Consumers should render `valueLabels[raw] ?? raw`; mapping payloads stay keyed on raw codes.
   */
  valueLabels?: Record<string, string> | null;
}

export interface IdentityActiveWorkspace {
  canonicalId: string;
  mode: IdentityWorkspaceMode;
  workspace: IdentityFieldWorkspace;
}

export interface IdentityRequiredFieldsProjection {
  items: IdentityFieldSummary[];
  readyToCreate: boolean;
  summaryLine: string;
}

export interface IdentityStepShell {
  sessionId: string;
  /** Canonical machine version for optimistic concurrency (If-Match / expectedVersion). */
  machineVersion: number;
  status: AudienceImportSessionStatus;
  createdAt: string;
  updatedAt: string;
  expiresAt: string | null;
  audienceDraft: AudienceImportSession["audienceDraft"];
  respondentCount: number;
  requiredFields: IdentityRequiredFieldsProjection | null;
  previewSync: AudienceImportSession["previewSync"];
  buildStatus: AudienceImportSession["buildStatus"];
  contextMaterials: AudienceImportSession["contextMaterials"];
  links: AudienceImportSession["links"];
  actions: AudienceImportSession["actions"];
  activeWorkspace?: IdentityActiveWorkspace | null;
}

export interface IdentityNextWorkspace {
  canonicalId: string;
  mode: IdentityWorkspaceMode;
}

export type IdentityFieldPatchRequest =
  | {
      expectedVersion?: number;
      action: "confirm_source";
      sourceColumn: string;
    }
  | {
      expectedVersion?: number;
      action: "set_constant";
      value: string;
    }
  | {
      expectedVersion?: number;
      action: "accept_inferred";
      customValues?: Array<{ value: string; percentage: number }>;
    }
  | {
      expectedVersion?: number;
      action: "save_value_map";
      sourceColumn: string;
      valueMap: Record<string, string>;
    };

export interface IdentityFieldPatchResponse {
  machineVersion: number;
  requiredFields: IdentityRequiredFieldsProjection | null;
  field: IdentityFieldSummary;
  nextWorkspace: IdentityNextWorkspace | null;
  activeWorkspace?: IdentityActiveWorkspace | null;
  routeAck?: AudienceImportRouteAck | null;
}

export interface SessionWarning {
  code: string;
  title: string;
  message: string;
  impact: "none" | "minor" | "medium" | "high";
}

export interface AudienceImportSession {
  // Target-contract naming only. In Phase 0 this is an adapter alias over live
  // staging `stageId`; no live payload is renamed by these scaffolds.
  sessionId: string;
  /** Canonical machine version for optimistic concurrency (If-Match / expectedVersion). */
  machineVersion: number;
  status: AudienceImportSessionStatus;
  createdAt: string;
  updatedAt: string;
  expiresAt: string | null;
  preflightTagCountStatus?: AudienceTagCountStatus | null;
  preflightTagCountManifestPath?: string | null;
  preflightTagCountError?: string | null;
  audienceDraft: {
    name: string;
    audienceType: AudienceType | null;
    tier: AudienceTier;
    sharingMode: "organization" | "private";
    tagline?: string;
    shortDescription?: string;
    subtitle?: string;
    description?: string;
    titleFont?: string;
    selectedCoverPhotoIndex?: number;
  };
  fileSummary: {
    fileName: string;
    fileSizeBytes: number;
    format: "csv" | "sav" | "xlsx" | "xls";
    uploadedAt: string;
    workbook: {
      sheetCount: number;
      requiresAssignment: boolean;
    } | null;
  } | null;
  sheetAssignment: {
    audienceSheet: string | null;
    dictionarySheet: string | null;
    ignoredSheets: string[];
    needsReview: boolean;
    options: Array<{
      sheetName: string;
      suggestedRole: "audience" | "dictionary" | "ignore";
      rowCount: number | null;
      columnCount: number | null;
    }>;
  } | null;
  requiredFields: {
    items: RequiredFieldSessionItem[];
    readyToCreate: boolean;
    summaryLine: string;
  } | null;
  previewSync: {
    status: "fresh" | "stale" | "refreshing";
    message: string | null;
    canRefresh: boolean;
  };
  // `analysis.stage === "ready"` means the analyze job completed. The durable
  // preview payload may still be pending, so `preview` can remain null while
  // the UI shows a stable pending state.
  analysis: {
    stage:
      | "queued"
      | "parsing"
      | "cleaning"
      | "mapping"
      | "ready"
      | "failed"
      | "cancelled";
    elapsedMs: number;
    summaryMessage: string | null;
  } | null;
  // Invariant: completed analyze sessions expose a durable preview payload that
  // can be refetched even if Redis/websocket state is lost.
  preview: {
    respondentCount: number;
    keptFieldCount: number | null;
    previewDataPartial?: boolean;
    review: PreviewReviewReadiness;
    summaryLine: string;
    autoHandledSummary: string[];
    respondents: PreviewRespondent[];
    issues: PreviewIssue[];
    readyToCreate: boolean;
    requiresRefresh: boolean;
    identityOverlayApplied: boolean;
    lastGeneratedAt: string;
    featuredTwin?: FeaturedPreviewTwin | null;
    coverPhotoOptions?: Array<{ url: string; thumbnailUrl: string }>;
  } | null;
  /**
   * Durable checkpoint stamped when the user explicitly continues from preview
   * into required-fields. This keeps "preview is ready" distinct from
   * "preview step has been completed."
   */
  previewStepCompletedAt?: string | null;
  buildStatus: {
    state: "idle" | "creating" | "finalizing" | "completed" | "failed";
    audienceId: string | null;
    summaryMessage: string | null;
    pendingJobs: Array<"description_tags">;
    errorMessage?: string;
  } | null;
  contextMaterials: {
    status: "pending" | "completed";
    enteredAt: string | null;
    draftStorageId: string | null;
    fileCount: number;
    hasPendingIndexing: boolean;
    files?: ContextMaterialFileItem[];
  };
  warnings: SessionWarning[];
  links: {
    preview: string;
    workbookAssignment?: string;
    contextMaterials: string;
    requiredFields: string;
    segmentation?: string;
    status: string;
    audienceDetail: string | null;
  };
  actions: {
    canReviewPreview: boolean;
    canShufflePreview: boolean;
    canRefreshPreview: boolean;
    canCreate: boolean;
    canRetryCreate: boolean;
    canRetryAnalyze: boolean;
    canDiscard: boolean;
  };
}

/**
 * Canonical flow snapshot for bootstrap and latest-flow surfaces.
 * Returned from createSession, getLatestSession, and the dedicated getFlowSnapshot read path.
 * Includes machine state, version, route, links, allowed actions, and branch
 * summaries sufficient for upload/resume behavior.
 */
export interface AudienceCreationFlowSnapshot {
  sessionId: string;
  /** Machine version for optimistic concurrency (If-Match). */
  machineVersion: number;
  /** Lifecycle state: active, creating, completed, discarded, expired, failed_terminal. */
  machineState: AudienceCreationFlowLifecycleState;
  /** Canonical backend-owned wizard phase for post-session orchestration. */
  wizardPhase?: AudienceImportWizardPhase;
  /** Backend-provided route target for redirect (resume target). */
  route: string;
  links: {
    workbookAssignment?: string;
    contextMaterials: string;
    requiredFields: string;
    segmentation?: string;
    status: string;
    audienceDetail: string | null;
  };
  actions: {
    canCreate: boolean;
    canRetryCreate: boolean;
    canDiscard: boolean;
  };
  /** Branch summaries sufficient for upload/resume behavior. */
  branchSummaries: {
    upload: AudienceCreationFlowUploadBranchSummary;
    preview: AudienceCreationFlowPreviewBranchSummary;
    identity: AudienceCreationFlowIdentityBranchSummary;
    contextMaterials: AudienceCreationFlowContextMaterialsBranchSummary;
    create: AudienceCreationFlowCreateBranchSummary;
  };
  /** Optional display fields for resume card (audience name, file, expiry). */
  audienceDraft?: { name: string };
  fileSummary?: {
    fileName: string;
    format?: "csv" | "sav" | "xlsx" | "xls";
  } | null;
  expiresAt?: string | null;
  /**
   * False while metadata preflight (load_and_suggest / column scan) has not
   * persisted yet — e.g. large uploads that defer work after the bootstrap response.
   * Identity and segmentation UIs should wait before offering column mapping.
   */
  hasPreflightData: boolean;
  /**
   * Async preflight lifecycle. Present on sessions created under the async flow.
   * Legacy sessions created before async preflight shipped may have this as null —
   * treat null as already-completed (their preflight data is either present or
   * never applicable).
   */
  preflightStatus?: AudienceImportPreflightStatus | null;
  /** Error details surfaced when preflightStatus is "rejected" or "failed". */
  preflightError?: {
    code: string;
    message: string;
    userAction?: string | null;
  } | null;
  preflightTagCountStatus?: AudienceTagCountStatus | null;
  preflightTagCountError?: string | null;
}

export type AudienceCreationFlowLifecycleState =
  | "active"
  | "creating"
  | "completed"
  | "discarded"
  | "expired"
  | "failed_terminal";

export type AudienceImportWizardPhase =
  | "new_import"
  | "workbook_assignment"
  | "context_materials"
  | "identity_review"
  | "segmentation"
  | "create_running"
  | "create_failed"
  | "completed"
  | "expired";

export interface AudienceCreationFlowUploadBranchSummary {
  /** empty | staged | workbook_assignment_required | ready_for_analyze */
  state: string;
  hasFile: boolean;
  requiresSheetAssignment: boolean;
  isMultiSheetWorkbook?: boolean;
}

export interface AudienceCreationFlowPreviewBranchSummary {
  /**
   * Segmentation readiness signal used by the wizard step rail.
   * "not_started" | "eligible" | "initiated"
   * - not_started: prerequisites not yet met (identity not confirmed)
   * - eligible: identity confirmed, segmentation step is accessible
   * - initiated: create workflow has been triggered
   */
  state: string;
  /** True once the create workflow has been triggered (used as segmentationDone in the frontend step rail). */
  readyToCreate: boolean;
}

export interface AudienceCreationFlowIdentityBranchSummary {
  /** unknown | needs_review | satisfied */
  state: string;
  readyToCreate: boolean;
}

export interface AudienceCreationFlowContextMaterialsBranchSummary {
  /** not_entered | entered | active | completed | skipped */
  state: string;
  fileCount?: number;
  hasPendingIndexing?: boolean;
  enteredAt?: string | null;
  draftStorageId?: string | null;
}

/**
 * Derives AudienceImportSessionStatus from the canonical flow snapshot.
 * Use this when merging routeAck or building session state from flowSnapshot.
 */
export function deriveAudienceImportStatusFromFlowSnapshot(
  snapshot: AudienceCreationFlowSnapshot,
): AudienceImportSessionStatus {
  const createState = snapshot.branchSummaries.create.state;
  const previewState = snapshot.branchSummaries.preview.state;
  if (snapshot.machineState === "expired") return "expired";
  if (snapshot.machineState === "completed") return "completed";
  if (createState === "failed") return "failed";
  if (createState === "finalizing") return "finalizing";
  if (createState === "queued" || createState === "creating") return "creating";
  if (previewState === "queued" || previewState === "running")
    return "analyzing";
  if (previewState === "failed") return "failed";
  if (previewState === "ready" || previewState === "eligible")
    return "preview_ready";
  // Align with mapFlowSnapshotStatus(requiredFieldsReady): create may be allowed
  // while preview branch is still not_started (e.g. preflight metadata lag).
  if (snapshot.actions.canCreate) {
    return "preview_ready";
  }
  return "draft";
}

/**
 * Derives the routeAck contextMaterials shape from the canonical flow snapshot.
 * Use when merging routeAck into projections that need contextMaterials.
 */
export function deriveRouteAckContextMaterialsFromFlowSnapshot(
  snapshot: AudienceCreationFlowSnapshot,
): {
  status: "pending" | "completed";
  enteredAt?: string | null;
  draftStorageId?: string | null;
  fileCount?: number;
  hasPendingIndexing?: boolean;
} {
  const cm = snapshot.branchSummaries.contextMaterials;
  const status =
    cm.state === "completed" || cm.state === "skipped"
      ? "completed"
      : "pending";
  return {
    status,
    enteredAt: cm.enteredAt,
    draftStorageId: cm.draftStorageId,
    fileCount: cm.fileCount,
    hasPendingIndexing: cm.hasPendingIndexing,
  };
}

/**
 * Derives the buildStatus shape (AudienceImportSession.buildStatus) from the canonical flow snapshot.
 * Use when merging routeAck into session or identity shell.
 */
export function deriveBuildStatusFromFlowSnapshot(
  snapshot: AudienceCreationFlowSnapshot,
): NonNullable<AudienceImportSession["buildStatus"]> {
  const create = snapshot.branchSummaries.create;
  const pendingJobs = create.pendingJobs ?? [];
  const summaryMessage = create.summaryMessage ?? null;
  const errorMessage = create.errorMessage ?? undefined;

  switch (create.state) {
    case "creating":
    case "queued":
      return {
        state: "creating",
        audienceId: create.audienceId,
        summaryMessage,
        pendingJobs,
        errorMessage,
      };
    case "finalizing":
      return {
        state: "finalizing",
        audienceId: create.audienceId,
        summaryMessage,
        pendingJobs,
        errorMessage,
      };
    case "completed":
      return {
        state: "completed",
        audienceId: create.audienceId,
        summaryMessage,
        pendingJobs,
        errorMessage,
      };
    case "failed":
      return {
        state: "failed",
        audienceId: create.audienceId,
        summaryMessage,
        pendingJobs,
        errorMessage,
      };
    case "not_started":
    case "eligible":
    default:
      return {
        state: "idle",
        audienceId: create.audienceId,
        summaryMessage,
        pendingJobs,
        errorMessage,
      };
  }
}

export interface AudienceCreationFlowCreateBranchSummary {
  /** not_started | eligible | queued | creating | finalizing | completed | failed */
  state: string;
  /** Exact backend phase: queued, dispatching, artifact_resolving, persisting_population, creating, post_processing, finalizing, completed, failed, cancelled. */
  phase?: string;
  audienceId: string | null;
  audienceDetailRoute?: string | null;
  summaryMessage?: string | null;
  pendingJobs?: Array<"description_tags">;
  errorMessage?: string | null;
  queuedAt?: string | null;
  startedAt?: string | null;
}

/**
 * Thin status projection for create/status pages.
 * Keeps canonical route and branch state without shipping the full session DTO.
 */
export interface AudienceImportStatusProjection {
  sessionId: string;
  machineVersion: number;
  updatedAt: string;
  expiresAt: string | null;
  flowSnapshot: AudienceCreationFlowSnapshot;
  /** Normalized narrative card lifecycle (matches preview readiness logic). */
  narrativeCardStatus?: NarrativeCardStatus | null;
  narrativeCardGenerationStartedAt?: string | null;
  /** True when a draft card exists on the session record. */
  hasNarrativeCardDraft?: boolean;
}

/**
 * Thin projection for context-materials page reads.
 * Keeps step-specific state plus canonical flow state without the full session DTO.
 */
export interface AudienceImportContextMaterialsProjection {
  sessionId: string;
  machineVersion: number;
  updatedAt: string;
  expiresAt: string | null;
  audienceDraft: {
    name: string;
  };
  contextMaterials: AudienceImportSession["contextMaterials"];
  flowSnapshot: AudienceCreationFlowSnapshot;
}

/**
 * Thin projection for workbook-assignment page reads.
 * Keeps workbook-specific state plus canonical flow state without the full session DTO.
 */
export interface AudienceImportWorkbookProjection {
  sessionId: string;
  machineVersion: number;
  updatedAt: string;
  expiresAt: string | null;
  audienceDraft: {
    name: string;
  };
  fileSummary: AudienceImportSession["fileSummary"];
  sheetAssignment: AudienceImportSession["sheetAssignment"];
  flowSnapshot: AudienceCreationFlowSnapshot;
}

/**
 * Thin route handoff for conflict/completion/progress paths.
 * Omits statusProjection (which duplicates flowSnapshot) to reduce payload size.
 * Use for websocket broadcasts and 409 conflict responses where full status
 * projection is not needed.
 */
export interface AudienceImportThinRouteAck {
  sessionId: string;
  machineVersion: number;
  flowSnapshot: AudienceCreationFlowSnapshot;
  previewProjection?: AudienceImportPreviewProjection | null;
}

/**
 * Full route handoff for mutations that need updatedAt/expiresAt and full status.
 * Phase 2: flowSnapshot is the canonical correctness contract. statusProjection
 * provides updatedAt/expiresAt for cache warming. Derive status, buildStatus,
 * contextMaterials via deriveAudienceImportStatusFromFlowSnapshot,
 * deriveBuildStatusFromFlowSnapshot, deriveRouteAckContextMaterialsFromFlowSnapshot.
 */
export interface AudienceImportRouteAck {
  sessionId: string;
  machineVersion: number;
  flowSnapshot: AudienceCreationFlowSnapshot;
  statusProjection: AudienceImportStatusProjection;
  previewProjection?: AudienceImportPreviewProjection | null;
}

export type ImportSessionUpdateReason =
  | "session_created"
  | "session_updated"
  | "session_expired"
  | "analyze_started"
  | "analyze_dispatched"
  | "analyze_progress"
  | "analyze_completed"
  | "analyze_failed"
  | "analyze_cancelled"
  | "analyze_restarted"
  | "preflight_completed"
  | "preflight_rejected"
  | "preflight_failed"
  | "preflight_tag_count_ready"
  | "preflight_tag_count_unavailable"
  | "preview_step_completed"
  | "preview_refresh_started"
  | "preview_shuffled"
  | "create_enqueued"
  | "create_progress"
  | "create_completed"
  | "create_failed"
  | "post_processing_updated"
  | "session_discarded"
  | "context_materials_entered"
  | "context_materials_completed"
  | "context_materials_updated"
  | "context_materials_file_added"
  | "context_materials_file_removed"
  | "context_materials_retry_requested"
  | "context_materials_indexing_updated"
  | "context_materials_cleanup_completed"
  | "preview_hydration_completed"
  | "preview_twin_enriched"
  | "narrative_precomputation_updated"
  | "socket_resynced_after_gap"
  | "visibility_resynced_after_gap"
  /**
   * Server-sent snapshot emitted to a single socket immediately after it joins
   * `import-session:{id}`. Import-session sockets are invalidation triggers, not
   * state carriers, so the "snapshot" is a refetch prompt: the client revalidates
   * over HTTP and converges on whatever it missed before joining the room.
   */
  | "socket_subscribed_sync";

/**
 * Async preflight (POST /populations/load_and_suggest_demographics with async_mode=true)
 * status. Progresses pending → completed | rejected | failed on webhook receipt.
 *
 * - ``pending``: webhook not yet received; UI shows waiting state for identity and segmentation.
 * - ``completed``: preflight payloads persisted, identity/segmentation data is ready to use.
 * - ``rejected``: Python rejection gate fired (empty/unreadable file); show error with retry.
 * - ``failed``: transport error or unexpected failure on the Python side; show error with retry.
 *
 * ``null`` means legacy sessions created before the async flow shipped; treat as completed.
 */
export type AudienceImportPreflightStatus =
  | "pending"
  | "completed"
  | "rejected"
  | "failed";

export type ImportSessionUpdateKind =
  | "user_edit"
  | "machine_progress"
  | "repair"
  | "cleanup"
  | "lifecycle"
  | "compatibility_projection";

export interface ImportSessionUpdatedEvent {
  sessionId: string;
  reason: ImportSessionUpdateReason;
  timestamp: string;
  machineVersion?: number | null;
  requestId?: string | null;
  kind?: ImportSessionUpdateKind | null;
  /** Thin ack (flowSnapshot only) or full ack (with statusProjection). Frontend can derive status from flowSnapshot when statusProjection is absent. */
  routeAck?: AudienceImportRouteAck | AudienceImportThinRouteAck | null;
  identityShell?: IdentityStepShell | null;
}

/**
 * Thin projection for preview page reads.
 * Keeps preview-specific data plus canonical flow state without the full session DTO.
 */
/**
 * Phase-0 style column group from preflight (matches frontend `Phase0Group`).
 * Used for segmentation filter dropdown grouping parity with mapping review.
 */
export interface AudienceImportSegmentFiltersColumnGroup {
  groupId: string;
  columns: string[];
  groupType: string;
  parentLabel?: string | null;
  detectionRoute?: string | null;
}

/**
 * Sentinel prefix used to carry a *group* alias through the flat `customLabels`
 * map sent on audience create. A grouped-question alias is expanded to one
 * entry per member column, keyed `GROUP_LABEL_OVERRIDE_KEY_PREFIX + sourceColumn`,
 * so the backend can attach `groupDisplayLabel` to each member's segmentation
 * mapping (single-column aliases keep using the bare `sourceColumn` key).
 *
 * The prefix contains characters that cannot appear in a real column name,
 * keeping single-column and group aliases unambiguous in the same map.
 */
export const GROUP_LABEL_OVERRIDE_KEY_PREFIX = "__grouplabel__::";

/** Build the `customLabels` key that carries a group alias for one member column. */
export function makeGroupLabelOverrideKey(sourceColumn: string): string {
  return `${GROUP_LABEL_OVERRIDE_KEY_PREFIX}${sourceColumn}`;
}

/**
 * Type-guard: returns `true` when `key` is a group-label sentinel key (created by
 * {@link makeGroupLabelOverrideKey}). Use this instead of a raw `startsWith` check
 * so the sentinel prefix stays in one place.
 */
export function isGroupLabelOverrideKey(key: string): boolean {
  return key.startsWith(GROUP_LABEL_OVERRIDE_KEY_PREFIX);
}

/**
 * If `key` is a group-label sentinel key, return the member `sourceColumn` it
 * targets; otherwise return null. Used by the backend to apply group aliases.
 */
export function parseGroupLabelOverrideKey(key: string): string | null {
  if (!isGroupLabelOverrideKey(key)) {
    return null;
  }
  const sourceColumn = key.slice(GROUP_LABEL_OVERRIDE_KEY_PREFIX.length);
  return sourceColumn.length > 0 ? sourceColumn : null;
}

/**
 * Ready-to-use segmentation filter data produced by Python preflight
 * (`load_and_suggest_demographics`). Nest persists and forwards without reshaping.
 */
/** Parser-derived column row in preflight `segmentFiltersPreflight.columnCatalog`. */
export interface AudienceImportSegmentFiltersColumnCatalogEntry {
  name: string;
  label: string;
  isOperational?: boolean;
  isDropped?: boolean;
  /** Python preflight: hide from identity required-field source picker only. */
  excludeFromIdentityPicker?: boolean;
  sampleValues?: string[];
}

export interface AudienceImportSegmentFiltersPreflight {
  /**
   * Column names classified operational by Phase 0 (exclude from segment filter picker).
   * Omitted on legacy persisted preflight rows before this field existed.
   */
  operationalColumns?: string[];
  /** Per-column sample/distinct values capped for filter UI (1–10 values per eligible column). */
  filterColumnSamples: Record<string, string[]>;
  columnGroups: Array<AudienceImportSegmentFiltersColumnGroup>;
  /**
   * Full parsed column metadata from Python preflight. Omitted on legacy sessions.
   */
  columnCatalog?: Array<AudienceImportSegmentFiltersColumnCatalogEntry>;
}

/** Segment filter picker row built by Nest from preflight catalog + live mapping overlay. */
export interface AudienceImportSegmentFilterCandidate {
  sourceColumn: string;
  displayLabel: string;
  originalLabel: string;
  canonicalId: string | null;
  mappingSource: string;
  isOperational?: boolean;
  isDropped?: boolean;
  sampleValues: string[];
}

export interface AudienceImportPreviewProjection {
  sessionId: string;
  machineVersion: number;
  updatedAt: string;
  expiresAt: string | null;
  audienceDraft: {
    name: string;
    audienceType?: AudienceType | null;
    tagline?: string;
    shortDescription?: string;
    subtitle?: string;
    description?: string;
    titleFont?: string;
    selectedCoverPhotoIndex?: number;
  };
  /** Dataset row count (excluding header), available before analyze runs. */
  totalRows?: number | null;
  /** Best-known respondent count: preview → totalRows → required-fields coverage. */
  respondentCount: number;
  fileSummary: AudienceImportSession["fileSummary"];
  sheetAssignment: AudienceImportSession["sheetAssignment"];
  requiredFields: {
    readyToCreate: boolean;
    summaryLine: string;
  } | null;
  analysis: AudienceImportSession["analysis"];
  preview: AudienceImportSession["preview"];
  contextMaterials: AudienceImportSession["contextMaterials"];
  warnings: SessionWarning[];
  flowSnapshot: AudienceCreationFlowSnapshot;
  segmentationMappings: Array<{
    sourceColumn: string;
    canonicalId: string | null;
    displayLabel: string;
    mappingSource: string;
    isCustom?: boolean;
    isOperational?: boolean;
    valueLabels?: Record<string, string>;
    valueMap?: Record<string, string>;
  }>;
  constantContext: Record<string, string>;
  filterColumnSamples: Record<string, string[]>;
  /** Preflight-owned filter samples + column groups; prefer for grouping when present. */
  segmentFiltersPreflight?: AudienceImportSegmentFiltersPreflight | null;
  /**
   * Add-segment-filter candidates (catalog-backed). Prefer over deriving only from segmentationMappings.
   */
  segmentFilterCandidates?: Array<AudienceImportSegmentFilterCandidate>;
  narrativeCardDraft?: NarrativeCardDraft | null;
  narrativeCardStatus?: NarrativeCardStatus | null;
}

export interface ImportSessionSheetAssignmentPatch {
  audienceSheet: string;
  dictionarySheet?: string | null;
}

export type ImportSessionGuidedFixPatch =
  | {
      issueId: string;
      kind: "choose_column";
      canonicalId: string;
      sourceColumn: string;
    }
  | {
      issueId: string;
      kind: "set_constant";
      contextKey: string;
      value: string;
    }
  | {
      issueId: string;
      kind: "mark_not_provided";
      contextKey: string;
    }
  | {
      issueId: string;
      kind: "accept_inferred";
      canonicalId: string;
    }
  | {
      issueId: string;
      kind: "review_values";
      sourceColumn: string;
      valueMap: Record<string, string>;
    };

/**
 * Public generic PATCH contract for import sessions.
 * Supports only audienceDraft and expectedVersion.
 * Use explicit endpoints for sheet assignment and guided fixes:
 * - POST /import-sessions/:sessionId/assign-sheets
 * - POST /import-sessions/:sessionId/preview-guided-fix
 */
export interface ImportSessionPatch {
  expectedVersion?: number;
  audienceDraft?: Partial<AudienceImportSession["audienceDraft"]>;
  narrativeCardDraft?: NarrativeCardDraft;
}

/**
 * Context materials file item for session-scoped and population-scoped responses.
 * Staged indexing (F1): uploading → extracting → understanding → ready | failed.
 * Legacy `pending` / `active` remain valid on older rows.
 */
export interface ContextMaterialFileItem {
  id: string;
  originalFilename: string;
  fileSize: number;
  mimeType: string | null;
  /** Indexing state — see CONTEXT_MATERIAL_INDEXING_STATUS in context-materials.ts */
  indexingStatus: ContextMaterialIndexingStatus;
  /** Error message when indexing failed */
  indexingError?: string | null;
  createdAt: string;
}

/**
 * Response shape for context-materials file mutations (upload, remove).
 * Returns route ack + durable file list for refresh/resume.
 */
export interface ContextMaterialsFilesResponse {
  routeAck?: AudienceImportRouteAck | null;
  machineVersion?: number | null;
  files: ContextMaterialFileItem[];
}

/**
 * Minimal response shape for context-materials file listing/polling.
 * Avoids returning route state when the caller only needs file/indexing updates.
 */
export interface ContextMaterialsFilesListResponse {
  machineVersion: number | null;
  files: ContextMaterialFileItem[];
}

/**
 * Response shape for population-scoped context-materials file mutations (upload, remove, list).
 * No session; returns durable file list for audience detail.
 */
export interface PopulationContextMaterialsFilesResponse {
  files: ContextMaterialFileItem[];
}
